import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
  useMutation,
} from 'react-query';
import {
  CreateHouseholdParams,
  UpdateHouseholdParams,
  Household,
  HouseholdFinancial,
  HouseholdMember,
} from 'models/household';
import {
  createHousehold,
  createHouseholdFinancial,
  getHousehold,
  getHouseholdByUserId,
  getHouseholdFinancial,
  getHouseholdHeadByUserId,
  updateHousehold,
  updateHouseholdFinancial,
} from 'services/household';
import { useCurrentUser } from './useUser';

export const useCurrentHousehold = (
  queryOptions?: UseQueryOptions<Household>,
) => {
  const { currentUser } = useCurrentUser();
  const { data, ...rest } = useQuery<Household, unknown>(
    'currentHousehold',
    () => getHouseholdByUserId(currentUser?.id!),
    {
      enabled: !!currentUser?.id,
      ...queryOptions,
    },
  );
  return {
    currentHousehold: data,
    ...rest,
  };
};

export const useHousehold = (
  id: string | undefined,
  options: UseQueryOptions<Household> = {},
): Omit<UseQueryResult<Household>, 'data'> & {
  household: Household | undefined;
} => {
  const { data, ...rest } = useQuery<Household, unknown>(
    ['household', id],
    () => getHousehold(id),
    {
      ...options,
      enabled: !!id,
    },
  );
  return {
    household: data,
    ...rest,
  };
};

export const useHouseholdByUserId = (
  userId?: string,
  options: UseQueryOptions<Household> = {},
) => {
  const { data, ...rest } = useQuery<Household, unknown>(
    ['household', userId],
    () => getHouseholdByUserId(userId),
    {
      enabled: !!userId,
      ...options,
    },
  );
  return {
    household: data,
    ...rest,
  };
};

export const useCreateHousehold = (queryProps?: any) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ({ userId, data }: { userId: string; data: CreateHouseholdParams }) =>
      createHousehold(userId, data),
    queryProps,
  );

  return {
    createHousehold: mutate,
    createHouseholdAsync: mutateAsync,
    ...rest,
  };
};

export const useCurrentHouseholdMember = (
  options: UseQueryOptions<HouseholdMember> = {},
) => {
  const { currentUser } = useCurrentUser();
  const { data, ...rest } = useQuery<HouseholdMember, unknown>(
    'currentHouseholdMember',
    () => getHouseholdHeadByUserId(currentUser?.id!),
    {
      enabled: !!currentUser?.id,
      ...options,
    },
  );
  return {
    currentHouseholdMember: data,
    ...rest,
  };
};

export const useUpdateHousehold = (queryProps?: any) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ({ id, data }: { id: string; data: UpdateHouseholdParams }) =>
      updateHousehold(id, data),
    queryProps,
  );

  return {
    updateHousehold: mutate,
    updateHouseholdAsync: mutateAsync,
    ...rest,
  };
};

export const useHouseholdMemberByUserId = (
  userId?: string,
  options: UseQueryOptions<HouseholdMember> = {},
) => {
  const { data, ...rest } = useQuery<HouseholdMember, unknown>(
    ['householdMember', userId],
    () => getHouseholdHeadByUserId(userId),
    {
      enabled: !!userId,
      ...options,
    },
  );
  return {
    householdMember: data,
    ...rest,
  };
};

export const useHouseholdFinancial = (
  householdId?: string,
  taxYear?: number,
  options: UseQueryOptions<HouseholdFinancial> = {},
) => {
  const { data, ...rest } = useQuery<HouseholdFinancial, unknown>(
    ['householdFinancial', householdId, taxYear],
    () => getHouseholdFinancial(householdId, taxYear),
    {
      enabled: !!householdId && !!taxYear,
      ...options,
    },
  );
  return {
    householdFinancial: data,
    ...rest,
  };
};

export const useCreateHouseholdFinancial = (queryProps?: any) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ({
      householdId,
      taxYear,
      data,
    }: {
      householdId: string;
      taxYear: number;
      data: Partial<HouseholdFinancial>;
    }) => createHouseholdFinancial(householdId, taxYear, data),
    queryProps,
  );

  return {
    createHouseholdFinancial: mutate,
    createHouseholdFinancialAsync: mutateAsync,
    ...rest,
  };
};

export const useUpdateHouseholdFinancial = (queryProps?: any) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ({
      householdId,
      taxYear,
      data,
    }: {
      householdId: string;
      taxYear: number;
      data: Partial<HouseholdFinancial>;
    }) => updateHouseholdFinancial(householdId, taxYear, data),
    queryProps,
  );

  return {
    updateHouseholdFinancial: mutate,
    updateHouseholdFinancialAsync: mutateAsync,
    ...rest,
  };
};
